import React from 'react';
import { StyleSheet, View } from 'react-native';
import { colors, radii, spacing } from '../../theme';
import ConfirmSheet from '../ui/ConfirmSheet';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

type Props = {
  visible: boolean;
  /**
   * What was tapped - a service or a category, by name.
   *
   * Named back rather than left implicit: "coming soon" over nothing in
   * particular reads as the whole app being unavailable, when it is one trade
   * at one address.
   */
  subject: string | null;
  /**
   * The address it is not offered at, as the line already on screen. Empty
   * when there is no real address yet, and then the chip is not drawn.
   */
  address: string;
  /** Opens the address sheet - the one action that can turn this into a yes. */
  onChangeAddress: () => void;
  onClose: () => void;
};

/**
 * The answer to tapping a service that has not reached this area yet.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS AND NOT `CoverageSheet`
 * ---------------------------------------------------------------------------
 * `CoverageSheet` is about the CITY - the platform is not there at all, and it
 * stops a checkout. This is about one service in a city that IS covered: the
 * customer can book plenty here, just not this. Different facts, different
 * words, and reusing one sheet for both would tell someone in Indore that
 * Homingo is "coming to your city soon".
 *
 * Built on the same `ConfirmSheet` so the two look and behave as one family:
 * sized to its content, above the safe area, text that wraps at any font size,
 * on any phone.
 *
 * ---------------------------------------------------------------------------
 * WHY CHANGING THE ADDRESS IS THE PRIMARY BUTTON
 * ---------------------------------------------------------------------------
 * It is the only action that can change the answer. People often have more
 * than one address on file - their own and a parent's across town - and the
 * service may already be offered at the other one. "Not now" is the way out,
 * worded as a decision rather than as a failure.
 */
function AreaComingSoonSheet({
  visible,
  subject,
  address,
  onChangeAddress,
  onClose,
}: Props) {
  return (
    <ConfirmSheet
      visible={visible}
      icon="pin"
      /* Neutral. Nothing is wrong with the customer or their address, and
         nothing was lost - red would read as a refusal. */
      tone="neutral"
      title="We are coming soon to this area"
      body={
        subject
          ? `${subject} isn't available at this address yet. Try another address - it may already be offered there.`
          : "This isn't available at this address yet. Try another address - it may already be offered there."
      }
      confirmLabel="Change address"
      cancelLabel="Not now"
      onConfirm={onChangeAddress}
      onClose={onClose}
    >
      {address ? (
        <View style={styles.address}>
          <Icon name="pin" size={14} color={colors.inkMuted} />
          <Text
            variant="caption"
            color={colors.inkBody}
            numberOfLines={2}
            style={styles.addressText}
          >
            {address}
          </Text>
        </View>
      ) : null}
    </ConfirmSheet>
  );
}

const styles = StyleSheet.create({
  address: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
  },
  /* Zero minimum so a long address wraps instead of pushing the row wider
     than a narrow screen. */
  addressText: {
    flex: 1,
    minWidth: 0,
  },
});

export default React.memo(AreaComingSoonSheet);
